import { apiClient } from "@/lib/api";
import { withReadableBlobErrorBody } from "@/lib/blobErrorBody";
import { parseContentDispositionFilename } from "@/lib/contentDisposition";
import type {
  BillingSummary,
  DetailedInvoiceJob,
  TenantBillingRecord,
  TenantBillingRecordListResponse,
} from "@/types/billing";

/**
 * Filename for a downloaded invoice: the backend's `Content-Disposition` when
 * it sent one (it carries the real invoice number), else a stable fallback so
 * the file is never saved as an opaque blob id.
 */
export function invoiceFilename(headers: unknown, id: number): string {
  const disposition = (headers as Record<string, unknown> | undefined)?.[
    "content-disposition"
  ];
  return (
    parseContentDispositionFilename(
      typeof disposition === "string" ? disposition : undefined,
    ) ?? `invoice-${String(id)}.pdf`
  );
}

/**
 * Fetch the billing summary for the authenticated company_admin's tenant.
 * Returns current month on-the-fly stop count + recent invoice records.
 */
export async function getBillingSummary(): Promise<BillingSummary> {
  const response = await apiClient.get<BillingSummary>("/v1/billing/summary/");
  return response.data;
}

/**
 * One page of the authenticated company_admin's invoice history.
 *
 * Returns the paginated ENVELOPE, not just `results`: the page shows the
 * server's `count` as the history title («История счетов 6») and offers
 * «Показать ещё» only while `next` is non-null (TASK-5108 / Q3). The old
 * version returned `results` alone, which threw both away and silently capped
 * the history at the first page.
 */
export async function listBillingRecords(params?: {
  page?: number;
  page_size?: number;
  status?: string;
}): Promise<TenantBillingRecordListResponse> {
  const response = await apiClient.get<TenantBillingRecordListResponse>(
    "/v1/billing/records/",
    { params },
  );
  return response.data;
}

/**
 * Download the SUMMARY invoice PDF (TASK-4814 — the default, one click).
 *
 * Returns the PDF BYTES, not a URL. TASK-4813 changed this contract: the owner
 * ruled the document must render in the UI language on every download, and a
 * single stored S3 file cannot do that (whoever downloaded first would fix the
 * language for everyone after). So the backend re-renders per request and the
 * response body IS the file — `window.open` on it would open a blob the browser
 * never navigates to, which is why every caller must go through `downloadBlob`.
 *
 * The summary carries no per-stop table, so it renders in ~2-3s at any invoice
 * size and is safe to await inline. `lang` is sent explicitly, mirroring the UI
 * language the app already knows.
 */
export async function downloadBillingRecord(
  id: number,
  lang: string,
): Promise<{ blob: Blob; filename: string }> {
  // A refusal arrives as a Blob too (`responseType` applies to every
  // response), so its JSON body is parsed before the error leaves this
  // function — otherwise the server's reason never reaches the toast (BUG-704).
  const response = await apiClient
    .get<Blob>(`/v1/billing/records/${id}/download/`, {
      params: { lang },
      responseType: "blob",
    })
    .catch(async (err: unknown) => {
      throw await withReadableBlobErrorBody(err);
    });
  return {
    blob: response.data,
    filename: invoiceFilename(response.headers, id),
  };
}

/**
 * Ask for the DETAILED invoice PDF and report where that request got to.
 *
 * This does NOT return a file. The owner ruled the detailed render must happen
 * in the BACKGROUND — measured at 3s / 26s / 36s for 20 / 627 / 869 stops, and
 * one tenant has 12 849 monthly events, which would exceed nginx's 60s timeout
 * outright. So this starts (or joins) a render and returns immediately; poll
 * until `status` is `ready`, then fetch `download_url`. The user also gets an
 * in-app `invoice_ready` notification, so polling is a convenience rather than
 * the only signal.
 */
export async function requestDetailedBillingRecord(
  id: number,
  lang: string,
): Promise<DetailedInvoiceJob> {
  const response = await apiClient.post<DetailedInvoiceJob>(
    `/v1/billing/records/${id}/detailed/`,
    null,
    { params: { lang } },
  );
  return response.data;
}

export type {
  BillingSummary,
  DetailedInvoiceJob,
  TenantBillingRecord,
  TenantBillingRecordListResponse,
};
